/**
 * Scroll-triggered reveal for elements with `data-ausmed-reveal`.
 * Values: fade-up | fade | slide-left
 *
 * Per-element optional attributes:
 *   data-ausmed-reveal-delay  — extra delay in ms (used for stagger inside a section)
 *
 * Theme-level config (read from <html> data attributes set in `snippets/scripts.liquid`):
 *   data-ausmed-reveal-trigger          — % of element that must be visible to start reveal
 *   data-ausmed-reveal-base-delay-ms    — base delay (ms) added to every revealed element
 *
 * Honours `prefers-reduced-motion`: reveals immediately without animation.
 *
 * Dynamic content: after HTML is injected (e.g. `product-recommendations` fetch), dispatch
 * `document.dispatchEvent(new CustomEvent('ausmed:reveal-rescan', { detail: { root: element } }))`
 * so new `[data-ausmed-reveal]` nodes are observed. `root` is optional (defaults to `document`).
 */
(function initAusmedReveal() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function queryReveal(scope) {
    const root = scope && scope.nodeType === 1 ? scope : document;
    return root.querySelectorAll('[data-ausmed-reveal]:not(.is-revealed)');
  }

  function revealAllIn(scope) {
    queryReveal(scope).forEach((el) => el.classList.add('is-revealed'));
  }

  if (reduced) {
    revealAllIn(document);
    document.addEventListener('ausmed:reveal-rescan', function (e) {
      revealAllIn(e.detail && e.detail.root ? e.detail.root : document);
    });
    document.addEventListener('shopify:section:load', function () {
      revealAllIn(document);
    });
    return;
  }

  const html = document.documentElement;
  const triggerRaw = Number.parseInt(html.dataset.ausmedRevealTrigger || '25', 10);
  const triggerPct = Number.isFinite(triggerRaw) ? Math.min(60, Math.max(5, triggerRaw)) : 25;
  const threshold = Math.min(0.6, Math.max(0.05, triggerPct / 100));

  const baseDelayRaw = Number.parseInt(html.dataset.ausmedRevealBaseDelayMs || '0', 10);
  const baseDelayMs = Number.isFinite(baseDelayRaw) ? Math.max(0, baseDelayRaw) : 0;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        observer.unobserve(el);
        const itemRaw = el.getAttribute('data-ausmed-reveal-delay');
        const itemDelay = itemRaw != null && itemRaw !== '' ? Math.max(0, Number.parseInt(itemRaw, 10) || 0) : 0;
        const delayMs = baseDelayMs + itemDelay;
        window.setTimeout(() => {
          el.classList.add('is-revealed');
        }, delayMs);
      });
    },
    { rootMargin: '0px 0px -10% 0px', threshold }
  );

  function bindTargets(scope) {
    queryReveal(scope).forEach((el) => observer.observe(el));
  }

  bindTargets(document);

  document.addEventListener('ausmed:reveal-rescan', function (e) {
    const sub = e.detail && e.detail.root;
    bindTargets(sub && sub.nodeType === 1 ? sub : document);
  });

  document.addEventListener('shopify:section:load', function () {
    bindTargets(document);
  });
})();
